import { isBoolean, isNumber, isString, keys, map, reduce, size } from "lodash";
import { Color } from "@glossy";
import {
  getAllContentClassifications,
  getContentClassificationColor,
} from "@linkfluence/models/content-classification";
import {
  getAllZones,
  getZoneFromCountry,
} from "@linkfluence/models/countries-zone";
import { Country, getAllCountries } from "@linkfluence/models/country";
import {
  Device,
  OperatingSystem,
  PublishingMethod,
  getAllDeviceOperatingSystems,
  getAllDevicePublishingMethods,
  getAllDevices,
  getDeviceColor,
  getDevicePublishingMethodColor,
  getOperatingSystemColor,
} from "@linkfluence/models/device";
import {
  Emotion,
  getAllEmotions,
  getEmotionColor,
} from "@linkfluence/models/emotion";
import { Facet } from "@linkfluence/models/facet";
import { Gender, getGenderColor } from "@linkfluence/models/gender";
import {
  ImageRecognitionAge,
  ImageRecognitionEmotions,
  ImageRecognitionPersons,
  getImageRecognitionAgeColor,
  getImageRecognitionEmotionColor,
  getImageRecognitionPersonColor,
  getImageRecognitionPersonIcon,
} from "@linkfluence/models/image-recognition";
import {
  Language,
  getAllLanguages,
  getLanguageColors,
} from "@linkfluence/models/language";
import {
  Platform,
  getAllPlatforms,
  getPlatformColor,
} from "@linkfluence/models/platform";
import { Tone, getAllTones, getToneColor } from "@linkfluence/models/tone";
import {
  Category,
  getAllTopicCategories,
  getTopicFromCategory,
} from "@linkfluence/models/topic";
import { predefinedSort } from "@tootools/sort";

export const CLOSED = "closed";

export const SINGLE = "single";

export const OPENED = "opened";

export const MULTIPLE = "multiple";

const clean = (object: object): object => {
  return reduce(
    object,
    (acc, value, key: string) => {
      if (value !== null) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>
  );
};

function isImageRecognitionEmotionsFacetValue(
  facet: string,
  value: string | number | boolean
): value is ImageRecognitionEmotions {
  return facet === Facet.ImageRecognitionEmotions;
}

function isImageRecognitionPersonsFacetValue(
  facet: string,
  value: string | number | boolean
): value is ImageRecognitionPersons {
  return facet === Facet.ImageRecognitionPersons;
}

function isImageRecognitionAgeFacetValue(
  facet: string,
  value: string | number | boolean
): value is ImageRecognitionAge {
  return facet === Facet.ImageRecognitionAge;
}

const buildLanguagesFacet = (languages: Language[]): object => {
  const colors = getLanguageColors(languages);
  return {
    id: Facet.Languages,
    type: CLOSED,
    mode: SINGLE,
    icon: "bubble-dots",
    values: map(languages, (language) => {
      return {
        id: language.toLowerCase(),
        color: colors[language],
      };
    }),
  };
};

const getFacetValueExtended = (
  facet: string,
  value: string | number | boolean
) => {
  if (isBoolean(value)) {
    return {};
  }
  switch (facet) {
    case Facet.ReviewRate:
      return {
        "0": {
          min: 0,
          max: 0,
          color: "#000000",
        },
        "1": {
          min: 0.1,
          max: 30,
          color: "#E64C65",
        },
        "2": {
          min: 30.1,
          max: 50,
          color: "#FF9933",
        },
        "3": {
          min: 50.1,
          max: 70,
          color: "#FFCC00",
        },
        "4": {
          min: 70.1,
          max: 99.9,
          color: "#ABCF02",
        },
        "5": {
          min: 100,
          max: 100,
          color: Color.LightSeaGreen,
        },
      }[("" + value) as "0" | "1" | "2" | "3" | "4" | "5"];
    case Facet.AuthorAge:
    case Facet.AuthorBirthday:
      return {
        "13-": {
          min: 0,
          max: 12,
        },
        "13-17": {
          min: 13,
          max: 17,
        },
        "18-24": {
          min: 18,
          max: 24,
        },
        "25-34": {
          min: 25,
          max: 34,
        },
        "35-44": {
          min: 35,
          max: 44,
        },
        "45-54": {
          min: 45,
          max: 54,
        },
        "55-64": {
          min: 55,
          max: 64,
        },
        "65+": {
          min: 65,
          max: 110,
        },
      }[
        ("" + value) as
          | "65+"
          | "55-64"
          | "45-54"
          | "35-44"
          | "25-34"
          | "18-24"
          | "13-17"
          | "13-"
      ];
    case Facet.Countries:
      return {
        category: getZoneFromCountry(value as Country),
      };
    case Facet.Topics:
      return {
        category: getTopicFromCategory(value as Category),
      };
    default:
      return {};
  }
};

const getFacetValueColor = (
  facet: string,
  value: string | number | boolean
): string | null => {
  if (isBoolean(value)) {
    return null;
  } else {
    if (facet === Facet.Platforms) {
      return getPlatformColor(value as Platform);
    } else if (
      facet === Facet.ImageRecognitionGenders ||
      facet === Facet.Genders
    ) {
      return getGenderColor(value as Gender);
    } else if (isImageRecognitionPersonsFacetValue(facet, value)) {
      return getImageRecognitionPersonColor(value);
    } else if (isDevicesPublishingMethodsFacet(facet, value)) {
      return getDevicePublishingMethodColor(value);
    } else if (isDeviceOperatingSystemsFacet(facet, value)) {
      return getOperatingSystemColor(value);
    } else if (isDevicesFacet(facet, value)) {
      return getDeviceColor(value);
    } else if (facet === Facet.Tones) {
      return getToneColor(value as Tone);
    } else if (isEmotionsFacet(facet, value)) {
      return getEmotionColor(value);
    } else if (isImageRecognitionAgeFacetValue(facet, value)) {
      return getImageRecognitionAgeColor(value);
    } else if (isImageRecognitionEmotionsFacetValue(facet, value)) {
      return getImageRecognitionEmotionColor(value);
    } else if (facet === Facet.ContentClassification) {
      return getContentClassificationColor("" + value);
    } else {
      return null;
    }
  }
};

function isDevicesPublishingMethodsFacet(
  facet: string,
  value: string | number | boolean
): value is PublishingMethod {
  return facet === Facet.DevicesPublishingMethods;
}

function isDeviceOperatingSystemsFacet(
  facet: string,
  value: string | number | boolean
): value is OperatingSystem {
  return facet === Facet.DeviceOperatingSystems;
}

function isDevicesFacet(
  facet: string,
  value: string | number | boolean
): value is Device {
  return facet === Facet.Devices;
}

function isEmotionsFacet(
  facet: string,
  value: string | number | boolean
): value is Emotion {
  return facet === Facet.Emotions;
}

function getToneIcon(tone: Tone) {
  return {
    [Tone.Positive]: "tone-positive",
    [Tone.Negative]: "tone-negative",
    [Tone.Neutral]: "tone-neutral",
    [Tone.Mixed]: "tone-mixed",
  }[tone];
}

const getFacetValueIcon = (
  facet: string,
  value: string | boolean | number
): string | null => {
  if (isNumber(value) || isBoolean(value)) {
    return null;
  } else {
    if (facet === Facet.Platforms) {
      return value;
    } else if (isImageRecognitionPersonsFacetValue(facet, value)) {
      return getImageRecognitionPersonIcon(value);
    } else if (facet === Facet.Tones) {
      return getToneIcon(value as Tone);
    } else {
      return null;
    }
  }
};

const getFacetValueId = (facet: string, value: string | number | boolean) => {
  if (facet === Facet.Countries && isString(value)) {
    return value.toLowerCase();
  } else {
    return value;
  }
};

const getSortedValues = (
  facet: Facet,
  values: Array<string | number | boolean> = []
) => {
  const toReturn = map(values, (value) => {
    return clean({
      id: getFacetValueId(facet, value),
      color: getFacetValueColor(facet, value),
      icon: getFacetValueIcon(facet, value),
      ...getFacetValueExtended(facet, value),
    });
  });

  switch (facet) {
    case Facet.Countries:
      return predefinedSort(toReturn, "category", keys(getAllZones()));
    default:
      return toReturn;
  }
};

const buildFacet =
  (type: string, mode: string) =>
  (
    facet: Facet,
    icon: string,
    values: (string | number | boolean)[] | (string | number | boolean)[] = [],
    alreadyFormated: boolean = false
  ): object => {
    return {
      id: facet,
      type,
      mode,
      icon,
      values: alreadyFormated ? values : getSortedValues(facet, values),
    };
  };

const buildClosedSingleFacet = buildFacet(CLOSED, SINGLE);

const buildClosedMultipleFacet = buildFacet(CLOSED, MULTIPLE);

const buildOpenedMultipleFacet = buildFacet(OPENED, MULTIPLE);

const buildOpenedSingleFacet = buildFacet(OPENED, SINGLE);

const getValues = (
  values: Array<number | string | boolean>,
  globalValues: any
): any => {
  return size(values) ? values : globalValues;
};

interface BuildOptions {
  [Facet.ImageRecognitionLogos]: any;
  [Facet.ImageRecognitionPersonIdentifications]: any;
  [Facet.Countries]: any;
  [Facet.Platforms]: any;
  [Facet.Languages]: any;
  [Facet.Focuses]: any;
  [Facet.InfluencersTags]: any;
  [Facet.Corpus]: any;
  [Facet.GeoLocations]: any;
  [Facet.GeoTags]: any;
}

export function build(options: Partial<BuildOptions> = {}): object {
  return {
    [Facet.Platforms]: buildClosedSingleFacet(
      Facet.Platforms,
      "rings",
      getValues(options[Facet.Platforms], getAllPlatforms())
    ),
    [Facet.Query]: buildClosedSingleFacet(Facet.Query, "rings"),
    [Facet.Languages]: buildLanguagesFacet(
      getValues(options[Facet.Languages], getAllLanguages())
    ),
    [Facet.Countries]: buildClosedSingleFacet(
      Facet.Countries,
      "flag",
      getValues(options[Facet.Countries], getAllCountries())
    ),
    [Facet.ImageRecognitionLogos]: buildClosedMultipleFacet(
      Facet.ImageRecognitionLogos,
      "corners-plus",
      options[Facet.ImageRecognitionLogos],
      true
    ),
    [Facet.ImageRecognitionPersonIdentifications]: buildClosedMultipleFacet(
      Facet.ImageRecognitionPersonIdentifications,
      "corners-plus",
      options[Facet.ImageRecognitionPersonIdentifications],
      true
    ),
    [Facet.Focuses]: buildClosedMultipleFacet(
      Facet.Focuses,
      "folder-magnifier",
      options[Facet.Focuses],
      true
    ),
    [Facet.Corpus]: buildClosedMultipleFacet(
      Facet.Corpus,
      "target",
      options[Facet.Corpus],
      true
    ),
    [Facet.InfluencersTags]: buildClosedMultipleFacet(
      Facet.InfluencersTags,
      "user-tag",
      options[Facet.InfluencersTags],
      true
    ),
    [Facet.Occupations]: buildOpenedMultipleFacet(
      Facet.Occupations,
      "suitcase"
    ),
    [Facet.DeviceDisplayNames]: buildOpenedMultipleFacet(
      Facet.DeviceDisplayNames,
      "device"
    ),
    [Facet.Regions]: buildClosedSingleFacet(Facet.Regions, "flag"),
    [Facet.Towns]: buildClosedSingleFacet(Facet.Towns, "town"),
    [Facet.Emoji]: buildOpenedMultipleFacet(Facet.Emoji, "emoji"),
    [Facet.EmojiCharts]: buildOpenedMultipleFacet(Facet.EmojiCharts, "emoji"),
    [Facet.EmojiAnnotations]: buildOpenedMultipleFacet(
      Facet.EmojiAnnotations,
      "emoji"
    ),
    [Facet.Affects]: buildOpenedMultipleFacet(Facet.Affects, "smiley-tag"),
    [Facet.AffectsPositive]: buildOpenedMultipleFacet(
      Facet.AffectsPositive,
      "smiley-tag"
    ),
    [Facet.AffectsNegative]: buildOpenedMultipleFacet(
      Facet.AffectsNegative,
      "smiley-tag"
    ),
    [Facet.Keywords]: buildOpenedMultipleFacet(Facet.Keywords, "tag"),
    [Facet.Mentions]: buildOpenedMultipleFacet(Facet.Mentions, "arobase"),
    [Facet.Hashtags]: buildOpenedMultipleFacet(Facet.Hashtags, "tag"),
    [Facet.Entities]: buildOpenedMultipleFacet(Facet.Entities, "tag"),
    [Facet.Keyphrases]: buildOpenedMultipleFacet(Facet.Keyphrases, "tag"),
    [Facet.MaritalStatus]: buildClosedSingleFacet(
      Facet.MaritalStatus,
      "wedding-rings",
      ["relationship", "children"]
    ),
    [Facet.Media]: buildClosedSingleFacet(Facet.Media, "polaroid", [
      "video",
      "image",
    ]),
    [Facet.ImageRecognitionGenders]: buildClosedSingleFacet(
      Facet.ImageRecognitionGenders,
      "genders",
      ["M", "F", "XX"]
    ),
    [Facet.ImageRecognitionEmotions]: buildClosedSingleFacet(
      Facet.ImageRecognitionEmotions,
      "mask",
      ["happy", "angry"]
    ),
    [Facet.ImageRecognitionAge]: buildClosedSingleFacet(
      Facet.ImageRecognitionAge,
      "candles",
      ["children", "teenagers", "young_adults", "adults", "seniors"]
    ),
    [Facet.Emotions]: buildClosedSingleFacet(
      Facet.Emotions,
      "masks",
      getAllEmotions()
    ),
    [Facet.ImageRecognitionPersons]: buildClosedSingleFacet(
      Facet.ImageRecognitionPersons,
      "users-3",
      ["none", "solo", "duo", "group", "crowd"]
    ),
    [Facet.DevicesPublishingMethods]: buildClosedSingleFacet(
      Facet.DevicesPublishingMethods,
      "device",
      getAllDevicePublishingMethods()
    ),
    [Facet.ProfileType]: buildClosedSingleFacet(Facet.ProfileType, "", [
      "human",
    ]),
    [Facet.SpamType]: buildClosedSingleFacet(Facet.SpamType, "", ["sale"]),
    [Facet.AccountType]: buildClosedSingleFacet(
      Facet.AccountType,
      "instagram",
      ["personal", "business", "undetected"]
    ),
    [Facet.Genders]: buildClosedSingleFacet(Facet.Genders, "genders", [
      "M",
      "F",
    ]),
    [Facet.TwitterCertified]: buildClosedSingleFacet(
      Facet.TwitterCertified,
      "verified",
      [true, false]
    ),
    [Facet.AuthorType]: buildClosedSingleFacet(Facet.AuthorType, "author", [
      "me",
      "other",
    ]),
    // @deprecated
    [Facet.Favorites]: buildClosedSingleFacet(Facet.Favorites, "star", [
      true,
      false,
    ]),
    [Facet.Favorite]: buildClosedSingleFacet(Facet.Favorite, "star", [
      true,
      false,
    ]),
    [Facet.Trash]: buildClosedSingleFacet(Facet.Trash, "star", [true, false]),
    [Facet.UserUpdated]: buildClosedSingleFacet(Facet.UserUpdated, "tag-cog", [
      false,
      true,
    ]),
    [Facet.Retweets]: buildClosedSingleFacet(Facet.Retweets, "retweet", [true]),
    [Facet.Retweeters]: buildClosedSingleFacet(Facet.Retweeters, "retweet", [
      true,
    ]),
    [Facet.DeviceOperatingSystems]: buildClosedSingleFacet(
      Facet.DeviceOperatingSystems,
      "device",
      getAllDeviceOperatingSystems()
    ),
    [Facet.Topics]: buildClosedSingleFacet(
      Facet.Topics,
      "circles",
      getAllTopicCategories()
    ),
    [Facet.ContentClassification]: buildClosedSingleFacet(
      Facet.ContentClassification,
      "circles",
      getAllContentClassifications()
    ),
    [Facet.AuthorFollowers]: {
      ...buildOpenedSingleFacet(Facet.AuthorFollowers, "users-2"),
      metas: {
        min: 0,
        max: 1000000,
      },
    },
    [Facet.Followers]: {
      ...buildOpenedSingleFacet(Facet.Followers, "users-2"),
      metas: {
        min: null,
        max: null,
      },
    },
    [Facet.Devices]: buildClosedSingleFacet(
      Facet.Devices,
      "device",
      getAllDevices()
    ),
    [Facet.Tones]: buildClosedSingleFacet(Facet.Tones, "smiley", getAllTones()),
    [Facet.AuthorAge]: buildClosedSingleFacet(Facet.AuthorAge, "candles", [
      "13-",
      "13-17",
      "18-24",
      "25-34",
      "35-44",
      "45-54",
      "55-64",
      "65+",
    ]),
    [Facet.AuthorBirthday]: buildClosedSingleFacet(
      Facet.AuthorBirthday,
      "candles",
      ["13-", "13-17", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"]
    ),
    [Facet.ReviewRate]: buildClosedSingleFacet(Facet.ReviewRate, "star-half", [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
    ]),
    [Facet.SharedUrl]: buildOpenedSingleFacet(Facet.SharedUrl, "link"),
    [Facet.ImageRecognition]: buildOpenedSingleFacet(
      Facet.ImageRecognition,
      "camera"
    ),
    [Facet.ImageRecognitionObjects]: buildOpenedSingleFacet(
      Facet.ImageRecognitionObjects,
      "camera"
    ),
    [Facet.ImageRecognitionScenes]: buildOpenedSingleFacet(
      Facet.ImageRecognitionScenes,
      "camera"
    ),
    [Facet.ResolvedEntities]: buildOpenedMultipleFacet(
      Facet.ResolvedEntities,
      "organization"
    ),
    [Facet.Demography]: buildOpenedSingleFacet(Facet.Demography, "genders"),
    [Facet.PictureWall]: buildOpenedSingleFacet(Facet.PictureWall, ""),
    [Facet.Product]: buildClosedMultipleFacet(Facet.Product, ""),
    [Facet.ProductRange]: buildClosedMultipleFacet(Facet.ProductRange, ""),
    [Facet.Franchise]: buildClosedMultipleFacet(Facet.Franchise, ""),
    [Facet.SignificantEntities]: buildOpenedMultipleFacet(
      Facet.SignificantEntities,
      "tag"
    ),
    [Facet.SignificantHashtags]: buildOpenedMultipleFacet(
      Facet.SignificantHashtags,
      "tag"
    ),
    [Facet.SignificantMentions]: buildOpenedMultipleFacet(
      Facet.SignificantMentions,
      "arobase"
    ),
    [Facet.SignificantNamedEntities]: buildOpenedMultipleFacet(
      Facet.SignificantNamedEntities,
      "tag"
    ),
    [Facet.SignificantKeyphrases]: buildOpenedMultipleFacet(
      Facet.SignificantKeyphrases,
      "tag"
    ),
    [Facet.EmergingHashtags]: buildOpenedMultipleFacet(
      Facet.EmergingHashtags,
      "tag"
    ),
    [Facet.EmergingMentions]: buildOpenedMultipleFacet(
      Facet.EmergingMentions,
      "arobase"
    ),
    [Facet.EmergingNamedEntities]: buildOpenedMultipleFacet(
      Facet.EmergingNamedEntities,
      "tag"
    ),
    [Facet.EmergingKeyphrases]: buildOpenedMultipleFacet(
      Facet.EmergingKeyphrases,
      "tag"
    ),
    [Facet.GeoLocations]: buildOpenedMultipleFacet(
      Facet.GeoLocations,
      "geomark",
      options[Facet.GeoLocations],
      true
    ),
    [Facet.GeoTags]: buildOpenedMultipleFacet(
      Facet.GeoTags,
      "geomarks",
      options[Facet.GeoTags],
      true
    ),
    [Facet.Towns]: buildClosedSingleFacet(
      Facet.Towns,
      "town",
      options[Facet.Towns],
      true
    ),
    [Facet.MaritalStatus]: buildClosedSingleFacet(
      Facet.MaritalStatus,
      "wedding-rings",
      ["relationship", "children"]
    ),
    [Facet.DocIds]: buildClosedSingleFacet(Facet.DocIds, "", [""], true),
  };
}
